import { ListItem, Meta, theme } from 'aimo-ui';
import Icon from 'components/icons/Icon';
import FeatureFlagsContext from 'context/FeatureFlagsContext';
import { format } from 'date-fns';
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, Text } from 'react-native';
import Animated from 'react-native-reanimated';
import { getLocale } from 'utils/commonUtils';
import { showFeatureFlag } from 'utils/featureFlagUtils';
import { testIdentifiers } from 'utils/testIdentifiers';
import { ListItemWrapper, Row } from './styles';

const styles = StyleSheet.create({
  normalText: {
    color: theme.colors.black,
    fontFamily: theme.fontFamily.medium,
    fontSize: theme.normalising.fontPixel(14),
  },
  highlightedText: {
    backgroundColor: theme.colors.infoYellow,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
  },
});

const RightContent = ({
  text,
  testID,
  showArrow,
  vehicleChanged,
  hasBeenPressed,
  highlight,
}: {
  text: string;
  testID?: string;
  showArrow?: boolean;
  vehicleChanged?: boolean;
  hasBeenPressed?: boolean;
  highlight?: boolean;
}) => {
  const {
    flags: { prepaid_display_valid_from_picker },
  } = useContext(FeatureFlagsContext);
  const showValidFromPicker = showFeatureFlag(
    prepaid_display_valid_from_picker
  );
  const AnimatedTextInput = Animated.createAnimatedComponent<any>(Text);

  return (
    <Row>
      <AnimatedTextInput
        {...testIdentifiers(`${testID}-content`)}
        style={[
          styles.normalText,
          showValidFromPicker && vehicleChanged && !hasBeenPressed && highlight
            ? styles.highlightedText
            : undefined,
        ]}>
        {text}
      </AnimatedTextInput>
      {showArrow && <Icon testID={`${testID}-icon`} name="Chevron_Right" />}
    </Row>
  );
};

export const truncateToMinute = (date: Date) => {
  const truncatedDate = new Date(date);
  truncatedDate.setSeconds(0, 0);
  return truncatedDate;
};

const ValidFromSelector = ({
  testID,
  value,
  showDateSelector,
  onPress,
  mode = 'date',
  vehicleChanged = false,
  hasBeenPressed = false,
  backgroundColor = theme.colors.lightBg.green,
}: {
  testID: string;
  value: Date;
  showDateSelector: boolean;
  onPress?: () => void;
  mode?: 'date' | 'time' | 'datetime';
  vehicleChanged?: boolean;
  hasBeenPressed?: boolean;
  backgroundColor?: string;
}) => {
  const { t } = useTranslation();
  const prevValueRef = useRef<Date | null>(null);
  const [textChanged, setTextChanged] = useState(false);

  useEffect(() => {
    if (prevValueRef.current === null) {
      prevValueRef.current = value;
      return;
    }

    const truncatedPrev = truncateToMinute(prevValueRef.current);
    const truncatedCurrent = truncateToMinute(value);
    if (truncatedPrev.getTime() !== truncatedCurrent.getTime()) {
      setTextChanged(true);
    }

    prevValueRef.current = value;
  }, [value]);

  const getValidDate = useCallback(
    (selectedDate: Date) => {
      const today = new Date();
      const locale = getLocale();
      const truncatedToday = truncateToMinute(today);
      const truncatedSelectedDate = truncateToMinute(selectedDate);

      if (mode === 'datetime') {
        if (
          truncatedToday.toISOString() === truncatedSelectedDate.toISOString()
        ) {
          return `${t('longTermParking.validImmediately')}`;
        }
        return format(selectedDate, 'd.M.yyyy HH:mm');
      }

      if (today.toDateString() === selectedDate.toDateString()) {
        return t('longTermParking.validImmediately');
      }
      return selectedDate.toLocaleDateString(locale.code);
    },
    [t, mode]
  );

  const RightComponent = useCallback(
    () => (
      <RightContent
        text={getValidDate(value)}
        testID={testID}
        showArrow={showDateSelector}
        vehicleChanged={vehicleChanged}
        hasBeenPressed={hasBeenPressed}
        highlight={textChanged}
      />
    ),
    [
      getValidDate,
      showDateSelector,
      testID,
      value,
      vehicleChanged,
      hasBeenPressed,
      textChanged,
    ]
  );

  const MetaComponent = useCallback(
    () => (
      <Meta color={theme.colors.gray300} testID={`${testID}-title`}>
        {t('longTermParking.valid')}
      </Meta>
    ),
    [t, testID]
  );

  return (
    <ListItemWrapper noBorder>
      <ListItem
        testID={testID}
        withBackground={backgroundColor}
        item={null}
        indexValue={0}
        titleComponent={MetaComponent}
        rightContentComponent={RightComponent}
        onPress={onPress}
      />
    </ListItemWrapper>
  );
};

export default ValidFromSelector;
